import { lazy, Suspense, useLayoutEffect, type CSSProperties } from 'react';
import { theme } from 'antd';
import { brandColor, fontFamilySerif } from '@titularis/ui';
import { Route, Routes, useLocation } from 'react-router';
import { Cabecalho } from './secoes/Cabecalho';
import { Heroi } from './secoes/Heroi';
import { ComoFunciona } from './secoes/ComoFunciona';
import { Modulos } from './secoes/Modulos';
import { Metodo } from './secoes/Metodo';
import { Duvidas } from './secoes/Duvidas';
import { Fechamento } from './secoes/Fechamento';
import { metaDaLanding } from './seo/paginas';
import { origemDoSite, useMeta } from './seo/useMeta';

// Páginas secundárias são carregadas quando abertas, inclusive o leitor de PDF.
const Noticias = lazy(() =>
  import('./paginas/Noticias').then((modulo) => ({ default: modulo.Noticias })),
);
const Provas = lazy(() =>
  import('./paginas/Provas').then((modulo) => ({ default: modulo.Provas })),
);
const Prova = lazy(() => import('./paginas/Prova').then((modulo) => ({ default: modulo.Prova })));
const Noticia = lazy(() =>
  import('./paginas/Noticia').then((modulo) => ({ default: modulo.Noticia })),
);
const Revista = lazy(() =>
  import('./paginas/Revista').then((modulo) => ({ default: modulo.Revista })),
);
const RevistaEdicao = lazy(() =>
  import('./paginas/RevistaEdicao').then((modulo) => ({ default: modulo.RevistaEdicao })),
);
const RevistaArtigo = lazy(() =>
  import('./paginas/RevistaArtigo').then((modulo) => ({ default: modulo.RevistaArtigo })),
);
const Materiais = lazy(() =>
  import('./paginas/Materiais').then((modulo) => ({ default: modulo.Materiais })),
);
const Material = lazy(() =>
  import('./paginas/Material').then((modulo) => ({ default: modulo.Material })),
);
const Descadastro = lazy(() =>
  import('./paginas/Descadastro').then((modulo) => ({ default: modulo.Descadastro })),
);
const Termos = lazy(() =>
  import('./paginas/Termos').then((modulo) => ({ default: modulo.Termos })),
);
const Privacidade = lazy(() =>
  import('./paginas/Privacidade').then((modulo) => ({ default: modulo.Privacidade })),
);
const NaoEncontrado = lazy(() =>
  import('./paginas/NaoEncontrado').then((modulo) => ({ default: modulo.NaoEncontrado })),
);

/**
 * A landing, numa página só.
 *
 * A ordem é a de um argumento, e não uma lista de assuntos: **o que é** (herói),
 * **como funciona** (os três passos), **por que acreditar** (método),
 * **o que existe hoje** (módulos), **e as
 * dúvidas que sobraram**. Cada seção responde à objeção que a anterior levanta.
 */
function Landing() {
  const { hash } = useLocation();
  // Ao voltar de outra rota, o navegador pode procurar a âncora antes do React.
  useLayoutEffect(() => {
    if (!hash) return;
    try {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: 'instant' });
    } catch {
      // Um fragmento malformado não deve impedir a renderização da landing.
    }
  }, [hash]);
  /*
   * A landing é a única tela cuja meta o `index.html` já traz pronta, montada
   * no build pelo plugin do `vite.config.ts`. O hook aqui existe para a
   * **volta**: quem abriu uma notícia e clicou no logotipo continua na mesma
   * página do navegador, e sem isto a landing ficaria com o título e o
   * canônico da notícia que acabou de sair da tela.
   */
  useMeta(metaDaLanding(origemDoSite()));

  return (
    <>
      <Heroi />
      <ComoFunciona />
      <Metodo />
      <Modulos />
      <Duvidas />
    </>
  );
}

/**
 * O site.
 *
 * **Ele ganhou rotas, e até 03/09/2026 não tinha nenhuma.** O comentário que
 * estava aqui dizia "sem rota - uma página de vendas com router é router a
 * mais", e estava certo enquanto a landing era a única coisa que morava neste
 * domínio. As Notícias Recentes mudaram a premissa: elas são públicas e sem
 * login, então não cabem no `apps/app`, que inteiro vive atrás do
 * `RequireAuth` - é o mesmo argumento que trouxe a landing para cá.
 *
 * O cabeçalho e o rodapé ficam fora do `Routes` porque são os mesmos nas três
 * telas, e é isso que faz a seção de notícias parecer parte do site em vez de
 * uma página solta com a marca no topo.
 */
export function App() {
  const { token } = theme.useToken();

  return (
    /*
      O fundo da pagina vem do token, e nao do `<style>` do index.html.
      A regra estatica la existe so para o instante antes do JavaScript rodar;
      se ela fosse a unica, o modo escuro ficaria com texto branco sobre fundo
      branco - foi exatamente o que aconteceu na primeira tentativa. As secoes
      nao tem fundo proprio, entao quem pinta e este envelope.
    */
    <div
      className="site"
      style={
        {
          background: token.colorBgContainer,
          minHeight: '100dvh',
          '--site-papel': token.colorBgContainer,
          '--site-tinta': token.colorText,
          '--site-secundario': token.colorTextSecondary,
          '--site-borda': token.colorBorderSecondary,
          '--site-acento': token.colorPrimary,
          '--site-marca': brandColor,
          '--site-suave': token.colorPrimaryBg,
          '--site-preenchimento': token.colorFillQuaternary,
          '--site-fundo': token.colorBgLayout,
          '--site-serifa': fontFamilySerif,
        } as CSSProperties
      }
    >
      <Cabecalho />
      <main id="conteudo" tabIndex={-1}>
        <Suspense
          fallback={
            <div className="pagina-carregando" role="status">
              Carregando…
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/noticias" element={<Noticias />} />
            <Route path="/noticias/:slug" element={<Noticia />} />
            {/*
            Provas para Imprimir mora aqui pelo mesmo motivo das Notícias: abre
            sem conta, e o `apps/app` inteiro vive atrás do `RequireAuth`. A
            diferença é que aqui o link **é** o produto - o módulo existe para
            que se mande o endereço de uma prova a quem pediu o PDF.
          */}
            <Route path="/provas" element={<Provas />} />
            <Route path="/provas/:slug" element={<Prova />} />
            <Route path="/revista" element={<Revista />} />
            <Route path="/revista/:edicao" element={<RevistaEdicao />} />
            <Route path="/revista/artigo/:slug" element={<RevistaArtigo />} />
            {/*
            Os materiais grátis moram aqui pelo motivo das Provas: o link **é** o
            produto. O endereço de link de dentro dos PDFs
            (`/materiais/:slug/l/:chave`) não passa pela SPA: o `vercel.json` o
            entrega à função `api/material.ts`, que resolve e redireciona.
          */}
            <Route path="/materiais" element={<Materiais />} />
            <Route path="/materiais/:slug" element={<Material />} />
            {/*
            O descadastro mora no site, e não no app, porque quem está numa
            lista de e-mails em geral não tem conta - e o app inteiro vive atrás
            do `RequireAuth`. Sair da lista não pode pedir login.
          */}
            <Route path="/descadastro" element={<Descadastro />} />
            {/*
            Os dois documentos legais moram no site pelo mesmo motivo das
            Notícias - abrem sem conta -, mas com um agravante próprio: quem os
            procura está decidindo se digita o número do cartão, e essa pessoa
            ainda não entrou. Eles também são os únicos endereços estáticos
            reconhecidos pelo `seo/rotas.ts`, porque o catch-all do
            `vercel.json` entrega todo robô àquela função e o que ela não
            reconhece vira 404.
          */}
            <Route path="/termos" element={<Termos />} />
            <Route path="/privacidade" element={<Privacidade />} />
            {/*
            **Endereço desconhecido tem tela própria desde 08/09/2026.** Ele
            caía na landing, e o argumento era bom para quem chegava de um link
            torto: a página de vendas responde melhor que um "404". O que esse
            arranjo não previa é o buscador, que não vê uma landing gentil - vê
            **200 com a home** em qualquer endereço inventado. Chama-se soft
            404, e a consequência é o índice encher de endereços que não
            existem, todos parecidos com a home, até ele passar a desconfiar do
            que o site responde.

            A tela nova não abre mão do argumento: ela diz que o endereço não
            existe e leva às provas, às notícias e à landing. O que ela
            acrescenta é o `noindex` - e o 404 de verdade, que a função da
            Vercel devolve a quem rastreia.
          */}
            <Route path="*" element={<NaoEncontrado />} />
          </Routes>
        </Suspense>
      </main>
      <Fechamento />
    </div>
  );
}
